import React, { type ReactNode, useCallback } from "react";

import classNames from "classnames";

import { Button } from "../Button/Button";

import style from "./SelectButton.module.css";

interface SelectButtonCheckboxProps {
  type: "checkbox";
  value: string[];
  onChange?: (value: string[]) => void;
  size?: "small" | "medium";
  children: ReactNode;
  disabled?: boolean;
  className?: string;
}

interface SelectButtonRadioProps {
  type: "radio";
  value: string;
  onChange?: (value: string) => void;
  size?: "small" | "medium";
  children: ReactNode;
  disabled?: boolean;
  className?: string;
}

export type SelectButtonProps = SelectButtonCheckboxProps | SelectButtonRadioProps;

type SelectButtonComponent<T> = React.FC<T> & {
  [key in keyof SelectButtonChildren]: SelectButtonChildren[key];
};

type SelectButtonChildren = {
  Item: typeof SelectButtonItem;
};

interface SelectButtonItemProps {
  title: string;
  subtitle?: ReactNode;
  value?: string;
  selected?: boolean;
  size?: "small" | "medium";
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  tabIndex?: number;
  "aria-checked"?: boolean;
  role?: string;
}

const SelectButtonItem: React.FC<SelectButtonItemProps> = ({
  title,
  subtitle,
  selected,
  size,
  disabled,
  onClick,
  className,
  tabIndex,
  "aria-checked": ariaChecked,
  role,
}) => {
  const handleOnClick = useCallback(() => {
    if (!disabled) {
      onClick?.();
    }
  }, [disabled, onClick]);

  if (size === "small") {
    return (
      <Button
        variant="grayscale"
        className={classNames(selected && style.selected, style.button)}
        disabled={disabled}
        onClick={handleOnClick}
        tabIndex={tabIndex}
        role={role}
        aria-checked={ariaChecked}
      >
        {title}
      </Button>
    );
  }

  return (
    <div
      className={classNames(
        style.item,
        selected && style.selected,
        disabled && style.disabled,
        className
      )}
      onClick={handleOnClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOnClick();
        }
      }}
      role={role ?? "button"}
      tabIndex={tabIndex}
      aria-checked={ariaChecked}
      aria-disabled={disabled || undefined}
    >
      <div className={style.itemContent}>
        <div className={style.title}>{title}</div>
        {subtitle && <div className={style.subtitle}>{subtitle}</div>}
      </div>
    </div>
  );
};

export const SelectButton: SelectButtonComponent<SelectButtonProps> = ({
  type,
  value,
  onChange,
  size,
  children,
  disabled,
  className,
}) => {
  const onSelectItem = useCallback(
    (val: string) => {
      if (type === "checkbox") {
        const checkboxValue = (value as string[]) ?? [];
        if (checkboxValue.includes(val)) {
          onChange?.(checkboxValue.filter((v) => v !== val));
        } else {
          onChange?.([...checkboxValue, val]);
        }
      } else {
        onChange?.(val);
      }
    },
    [onChange, value, type]
  );

  const handleItemClicked = useCallback(
    (val: string) => {
      if (!disabled) {
        onSelectItem(val);
      }
    },
    [disabled, onSelectItem]
  );

  // For radio groups: arrow keys move focus between items without changing selection.
  // Only Enter/Space (or click) actually selects an item.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (type !== "radio") return;
      if (
        e.key !== "ArrowDown" &&
        e.key !== "ArrowUp" &&
        e.key !== "ArrowLeft" &&
        e.key !== "ArrowRight"
      )
        return;

      e.preventDefault();

      const items = Array.from(
        e.currentTarget.querySelectorAll<HTMLElement>(
          '[role="radio"]:not([disabled]):not([aria-disabled="true"])'
        )
      );
      if (items.length === 0) return;

      const activeElement =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const activeItem =
        (activeElement?.closest('[role="radio"]') as HTMLElement | null) ??
        ((e.target as HTMLElement | null)?.closest('[role="radio"]') as HTMLElement | null);
      const currentIdx = activeItem ? items.indexOf(activeItem) : -1;
      const safeCurrentIdx = currentIdx >= 0 ? currentIdx : 0;
      const direction = e.key === "ArrowDown" || e.key === "ArrowRight" ? 1 : -1;
      const nextIdx = (safeCurrentIdx + direction + items.length) % items.length;
      items[nextIdx]?.focus();
    },
    [type]
  );

  return (
    <div
      className={classNames(
        style.wrapper,
        disabled && style.disabled,
        size === "small" && style.small,
        className
      )}
      role={type === "radio" ? "radiogroup" : undefined}
      onKeyDown={type === "radio" ? handleKeyDown : undefined}
    >
      {React.Children.map(children, (child) => {
        if (child && React.isValidElement<SelectButtonItemProps>(child)) {
          const itemValue = child.props.value;
          const itemDisabled = child.props.disabled ?? disabled;

          const radioProps =
            type === "radio" && itemValue
              ? {
                  role: "radio",
                  "aria-checked": itemValue === (value as string),
                  tabIndex: itemDisabled ? -1 : 0,
                }
              : {};

          return React.cloneElement(child, {
            size: child.props.size ?? size,
            selected: itemValue
              ? type === "radio"
                ? itemValue === (value as string)
                : (value as string[])?.includes(itemValue)
              : false,
            disabled: itemDisabled,
            onClick: () => itemValue && handleItemClicked(itemValue),
            ...radioProps,
          });
        }
        return child;
      })}
    </div>
  );
};
SelectButton.Item = SelectButtonItem;

SelectButtonItem.displayName = "SelectButtonItem";
